"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { Article, Category } from "@/types";
import Pagination from "../Pagination";
import CategoryArticlesSkeleton from "../skeleton/CategoryArticlesSkeleton";

type CategoryArticlesProps = {
  slug: string;
};

const CategoryArticles = ({ slug }: CategoryArticlesProps) => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [category, setCategory] = useState<Category | null>(null);

  // ✅ Fetch category info and related articles
  useEffect(() => {
    const fetchCategoryData = async () => {
      setLoading(true);

      try {
        // 🔹 Fetch category details by slug
        const [categoryResponse, articlesResponse] = await Promise.all([
          fetch(`/api/categories?slug=${slug}`),
          fetch(`/api/articles?page=${page}&pageSize=9&category=${slug}`),
        ]);

        // ✅ Parse responses
        const categoryData = await categoryResponse.json();
        const articlesData = await articlesResponse.json();

        if (!categoryData.success) throw new Error("Catégorie introuvable");
        if (!articlesData.success)
          throw new Error("Aucun article trouvé pour cette catégorie");

        setCategory(categoryData.categories[0]);
        setArticles(articlesData.articles || []);
        setTotalPages(articlesData.meta?.total || 1);

        // ✅ Smooth scroll after page change
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (error) {
        //console.error("Erreur lors du chargement des données :", error);
        setCategory(null);
        setArticles([]);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryData();
  }, [slug, page]); // ✅ Runs when `slug` or `page` changes

  if (loading) return <CategoryArticlesSkeleton />;

  return (
    <div>
      {/* ✅ Display Category Info */}
      <div className="mb-6 text-center">
        {category ? (
          <>
            <h1 className="text-3xl font-bold text-blue-600">
              {category.name}
            </h1>
            {category.description && (
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                {category.description}
              </p>
            )}
          </>
        ) : (
          <h1 className="text-3xl font-bold text-red-600">
            Catégorie introuvable
          </h1>
        )}
      </div>

      {/* ✅ Display Articles */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {articles.length > 0 ? (
          articles.map((article) => (
            <Link
              key={article.id}
              href={`/${article.slug}`}
              className="block rounded-lg border p-4 shadow hover:shadow-lg"
            >
              <Image
                src={
                  article.cover_url ? article.cover_url : "/default-image.jpg"
                }
                alt={article.title}
                width={400}
                height={250}
                className="h-48 w-full rounded-lg object-cover"
              />
              <h3 className="mt-3 text-lg font-semibold">{article.title}</h3>

              {/* ✅ Display Author Name + Publication Date */}
              <p className="text-sm text-gray-500">
                {article.authors?.[0]?.name ? (
                  <>
                    <span className="font-semibold">
                      {article.authors[0].name}
                    </span>
                    <span className="mx-2">•</span>
                  </>
                ) : null}
                {article.published_date}
              </p>
            </Link>
          ))
        ) : (
          <p className="col-span-full text-center text-gray-500">
            Aucun article trouvé.
          </p>
        )}
      </div>

      {/* ✅ Pagination with Flowbite */}
      {totalPages > 9 && (
        <div className="mt-8 flex flex-col items-center space-y-2">
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Page {page} sur {Math.ceil(totalPages / 9)}
          </span>
          <Pagination
            currentPage={page}
            totalPages={Math.ceil(totalPages / 9)}
            onPageChange={(newPage) => setPage(newPage)}
            showIcons
          />
        </div>
      )}
    </div>
  );
};

export default CategoryArticles;
